import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Share2, Users, DollarSign, TrendingUp, Copy, Loader2, CheckCircle, Clock, ArrowDownToLine, MousePointerClick, UserPlus, CreditCard, Bell, X } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantSaaSAffiliate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', bank_name: '', account_number: '', account_name: '' });
  const [withdrawing, setWithdrawing] = useState(false);
  // Notification bell state — polls every 30s so new commissions/withdrawals show up
  // without a full page refresh.
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.get('/tenant/saas-affiliate');
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/tenant/saas-affiliate/notifications?limit=20');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* silent — no notif until affiliate registered */ }
  };

  useEffect(() => { fetchData(); fetchNotifications(); }, []);
  useEffect(() => {
    const t = setInterval(fetchNotifications, 30000);
    return () => clearInterval(t);
  }, []);

  const markOneRead = async (id) => {
    try {
      await api.put(`/tenant/saas-affiliate/notifications/${id}/read`);
      fetchNotifications();
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.put('/tenant/saas-affiliate/notifications/read-all');
      fetchNotifications();
      toast.success('Semua notifikasi ditandai dibaca');
    } catch { /* ignore */ }
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      await api.post('/tenant/saas-affiliate/register');
      toast.success('Pendaftaran affiliate SaaS berhasil! Menunggu approval admin.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mendaftar');
    } finally {
      setRegistering(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawForm.amount || Number(withdrawForm.amount) <= 0) return toast.error('Masukkan jumlah withdrawal');
    if (!withdrawForm.bank_name || !withdrawForm.account_number || !withdrawForm.account_name) return toast.error('Lengkapi data bank');
    setWithdrawing(true);
    try {
      await api.post('/tenant/saas-affiliate/withdraw', {
        amount: Number(withdrawForm.amount),
        bank_name: withdrawForm.bank_name,
        account_number: withdrawForm.account_number,
        account_name: withdrawForm.account_name
      });
      toast.success('Request withdrawal berhasil dikirim');
      setShowWithdrawDialog(false);
      setWithdrawForm({ amount: '', bank_name: '', account_number: '', account_name: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal request withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/register?ref=${data.affiliate.code}`;
    navigator.clipboard.writeText(link);
    toast.success('Link referral disalin!');
  };

  const copyCode = () => {
    navigator.clipboard.writeText(data.affiliate.code);
    toast.success('Kode disalin!');
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  const statusBadge = (s) => {
    const colors = {
      active: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      inactive: 'bg-red-100 text-red-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      signup: 'bg-blue-100 text-blue-700',
      subscribed: 'bg-emerald-100 text-emerald-700',
      paid: 'bg-emerald-100 text-emerald-700',
    };
    return <Badge className={colors[s] || 'bg-slate-100 text-slate-700'}>{s}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  // Not registered yet
  if (!data?.affiliate) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Affiliate SaaS</h1>
        <Card className="max-w-lg mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-violet-600 rounded-2xl flex items-center justify-center mb-4">
              <Share2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle>Jadi Affiliate SaaS</CardTitle>
            <CardDescription>
              Dapatkan komisi dengan mereferensikan tenant baru ke platform Tokomoe.
              Setiap tenant yang mendaftar melalui link referral Anda dan berlangganan,
              Anda akan mendapatkan komisi.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <ul className="text-left text-sm text-slate-600 space-y-2 mb-6">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Komisi hingga 20% dari subscription</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Recurring commission setiap bulan</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Dashboard tracking lengkap</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Withdrawal mudah ke rekening bank</li>
            </ul>
            <Button onClick={handleRegister} disabled={registering} className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700">
              {registering ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Mendaftar...</> : 'Daftar Sekarang'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const aff = data.affiliate;
  const referrals = data.referrals || [];
  const commissions = data.commissions || [];
  const withdrawals = data.withdrawals || [];
  const referralLink = `${window.location.origin}/register?ref=${aff.code}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Affiliate SaaS</h1>
        <div className="flex items-center gap-3">
          {/* Notification Bell */}
          <div className="relative">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowNotifPanel(!showNotifPanel)}
              className="relative"
              title="Notifikasi"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>
            {showNotifPanel && (
              <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-[500px] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-3 border-b border-slate-200 bg-slate-50">
                  <p className="font-semibold text-slate-900">Notifikasi Affiliate</p>
                  <div className="flex items-center gap-1">
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs h-7">
                        Tandai dibaca
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => setShowNotifPanel(false)} className="h-7 w-7">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {notifications.length === 0 ? (
                    <div className="text-center py-8 px-4">
                      <Bell className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                      <p className="text-sm text-slate-500">Belum ada notifikasi</p>
                      <p className="text-xs text-slate-400 mt-1">Notif akan muncul saat ada referral baru, komisi masuk, atau update withdrawal.</p>
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => !n.is_read && markOneRead(n.id)}
                        className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${!n.is_read ? 'bg-blue-50/40' : ''}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${!n.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{n.title}</p>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-3 whitespace-pre-line">{(n.message || '').replace(/━/g, '').trim().slice(0, 200)}</p>
                            <p className="text-[10px] text-slate-400 mt-1">
                              {new Date(n.created_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {statusBadge(aff.status)}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50"><MousePointerClick className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Klik</p>
                <p className="text-xl font-bold">{fmt(aff.total_clicks)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-50"><UserPlus className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Signup</p>
                <p className="text-xl font-bold">{fmt(aff.total_signups)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-50"><CreditCard className="w-5 h-5 text-violet-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Subscribe</p>
                <p className="text-xl font-bold">{fmt(aff.total_subscriptions)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50"><DollarSign className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Total Komisi</p>
                <p className="text-xl font-bold">Rp {fmt(aff.total_commission)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link + Balance */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Link Referral</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Input value={referralLink} readOnly className="text-sm bg-slate-50" />
              <Button variant="outline" size="icon" onClick={copyLink}><Copy className="w-4 h-4" /></Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Kode:</span>
              <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">{aff.code}</code>
              <Button variant="ghost" size="sm" onClick={copyCode}><Copy className="w-3 h-3" /></Button>
            </div>
            <p className="text-xs text-slate-400">Bagikan link ini ke calon tenant baru. Komisi Anda: {aff.commission_rate}%</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Saldo Komisi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-slate-500">Pending</p>
                <p className="text-lg font-bold text-amber-600">Rp {fmt(aff.pending_commission)}</p>
              </div>
              <div>
                <p className="text-slate-500">Dicairkan</p>
                <p className="text-lg font-bold text-emerald-600">Rp {fmt(aff.withdrawn_commission)}</p>
              </div>
            </div>
            <Button
              onClick={() => setShowWithdrawDialog(true)}
              disabled={aff.status !== 'active' || (aff.pending_commission || 0) <= 0}
              className="w-full"
            >
              <ArrowDownToLine className="w-4 h-4 mr-2" /> Tarik Komisi
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Referrals, Commissions, Withdrawals */}
      <Tabs defaultValue="referrals">
        <TabsList>
          <TabsTrigger value="referrals">Referral ({referrals.length})</TabsTrigger>
          <TabsTrigger value="commissions">Komisi ({commissions.length})</TabsTrigger>
          <TabsTrigger value="withdrawals">Withdrawal ({withdrawals.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="referrals">
          <Card>
            <CardContent className="p-0">
              {referrals.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-400">Belum ada referral</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Subdomain</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tanggal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.tenant?.name || '-'}</TableCell>
                        <TableCell className="text-slate-500">{r.tenant?.subdomain || '-'}</TableCell>
                        <TableCell>{statusBadge(r.status)}</TableCell>
                        <TableCell className="text-slate-500">{fmtDate(r.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="commissions">
          <Card>
            <CardContent className="p-0">
              {commissions.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-400">Belum ada komisi</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipe</TableHead>
                      <TableHead>Jumlah</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tanggal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissions.map(c => (
                      <TableRow key={c.id}>
                        <TableCell>
                          <Badge variant="outline">{c.type || (c.month ? 'recurring' : 'initial')}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">Rp {fmt(c.amount)}</TableCell>
                        <TableCell>{statusBadge(c.status)}</TableCell>
                        <TableCell className="text-slate-500">{fmtDate(c.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="withdrawals">
          <Card>
            <CardContent className="p-0">
              {withdrawals.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-400">Belum ada withdrawal</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Jumlah</TableHead>
                      <TableHead>Bank</TableHead>
                      <TableHead>Rekening</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tanggal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {withdrawals.map(w => (
                      <TableRow key={w.id}>
                        <TableCell className="font-medium">Rp {fmt(w.amount)}</TableCell>
                        <TableCell>{w.bank_name || '-'}</TableCell>
                        <TableCell className="text-slate-500">{w.account_number || '-'}</TableCell>
                        <TableCell>{statusBadge(w.status)}</TableCell>
                        <TableCell className="text-slate-500">{fmtDate(w.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Withdraw Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tarik Komisi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Saldo tersedia: <strong>Rp {fmt(aff.pending_commission)}</strong></p>
            <div>
              <Label>Jumlah (Rp)</Label>
              <Input type="number" value={withdrawForm.amount} onChange={e => setWithdrawForm({ ...withdrawForm, amount: e.target.value })} placeholder="100000" />
            </div>
            <div>
              <Label>Nama Bank</Label>
              <Input value={withdrawForm.bank_name} onChange={e => setWithdrawForm({ ...withdrawForm, bank_name: e.target.value })} placeholder="BCA, Mandiri, BNI, dll" />
            </div>
            <div>
              <Label>Nomor Rekening</Label>
              <Input value={withdrawForm.account_number} onChange={e => setWithdrawForm({ ...withdrawForm, account_number: e.target.value })} placeholder="1234567890" />
            </div>
            <div>
              <Label>Nama Pemilik Rekening</Label>
              <Input value={withdrawForm.account_name} onChange={e => setWithdrawForm({ ...withdrawForm, account_name: e.target.value })} placeholder="Nama sesuai buku rekening" />
            </div>
            <Button onClick={handleWithdraw} disabled={withdrawing} className="w-full">
              {withdrawing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Memproses...</> : 'Kirim Request Withdrawal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
